/**
 * InstitutionsTab Component
 *
 * Content for the "Institutions" tab in SettingsDialog.
 * Shows connection health for all bank connections with status indicators,
 * sync buttons, and fallback options for errored connections.
 */

import { useBankConnections, useSyncConnection } from '@/features/accounts/hooks';
import { Icon, IconKey } from '@/shared/components/icon';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { BankConnection } from '@/shared/types';
import { cn, formatRelativeDate } from '@/shared/utils';
import { useState } from 'react';

// =============================================================================
// Status dot
// =============================================================================

function StatusDot({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full',
        status === 'active' && 'bg-success',
        status === 'error' && 'bg-destructive',
        status !== 'active' && status !== 'error' && 'bg-warning'
      )}
    />
  );
}

// =============================================================================
// Connection Row
// =============================================================================

interface ConnectionRowProps {
  connection: BankConnection;
  onReconnect: () => void;
  onFallback?: () => void;
}

function ConnectionRow({ connection, onReconnect, onFallback }: ConnectionRowProps) {
  const syncConnection = useSyncConnection();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncConnection.mutateAsync(connection.id);
    } finally {
      setIsSyncing(false);
    }
  };

  const isError = connection.status === 'error';
  const isActive = connection.status === 'active';

  return (
    <div className='flex items-start gap-3 py-3'>
      {/* Status dot */}
      <div className='mt-1.5'>
        <StatusDot status={connection.status} />
      </div>

      {/* Info */}
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-foreground truncate'>
            {connection.institution_name}
          </span>
          <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
            via {connection.provider === 'plaid' ? 'Plaid' : 'Teller'}
          </Badge>
        </div>
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <span>
            {connection.last_synced_at
              ? `Last synced ${formatRelativeDate(connection.last_synced_at)}`
              : 'Never synced'}
          </span>
          <span>·</span>
          <span>
            {connection.accounts_count} account{connection.accounts_count !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Error message */}
        {isError && connection.error_message && (
          <p className='mt-1 text-xs text-red-500 dark:text-red-400'>
            {connection.error_message}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className='flex items-center gap-1.5 shrink-0'>
        {isActive && (
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={handleSync}
            disabled={isSyncing}
            title='Sync now'
          >
            <Icon icon={IconKey.Refresh} size={14} className={cn(isSyncing && 'animate-spin')} />
          </Button>
        )}

        {isError && (
          <>
            <Button variant='outline' size='sm' className='h-7 text-xs' onClick={onReconnect}>
              Reconnect
            </Button>
            {connection.provider === 'teller' && onFallback && (
              <Button variant='outline' size='sm' className='h-7 text-xs' onClick={onFallback}>
                Try Plaid
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// InstitutionsTab
// =============================================================================

interface InstitutionsTabProps {
  onReconnect?: () => void;
  onFallback?: () => void;
}

export function InstitutionsTab({ onReconnect, onFallback }: InstitutionsTabProps) {
  const { data: connections, isLoading } = useBankConnections({ household: true });

  // Filter out manual connections
  const linkedConnections = connections?.filter((c) => c.provider !== 'manual') ?? [];

  if (isLoading) {
    return (
      <div className='space-y-3 py-2'>
        {[1, 2].map((i) => (
          <div key={i} className='flex items-center gap-3'>
            <Skeleton className='h-2 w-2 rounded-full' />
            <div className='flex-1 space-y-1.5'>
              <Skeleton className='h-4 w-32' />
              <Skeleton className='h-3 w-48' />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (linkedConnections.length === 0) {
    return (
      <div className='py-6 text-center'>
        <Icon icon={IconKey.Landmark} size={24} className='mx-auto mb-2 text-muted-foreground' />
        <p className='text-sm text-muted-foreground'>No bank connections yet.</p>
      </div>
    );
  }

  return (
    <div className='py-2'>
      <div className='flex items-center gap-2 mb-2'>
        <h3 className='text-sm font-medium'>Connections</h3>
        <Badge variant='secondary' className='text-[10px]'>
          {linkedConnections.length}
        </Badge>
      </div>

      <div className='divide-y'>
        {linkedConnections.map((conn) => (
          <ConnectionRow
            key={conn.id}
            connection={conn}
            onReconnect={() => onReconnect?.()}
            onFallback={conn.provider === 'teller' ? () => onFallback?.() : undefined}
          />
        ))}
      </div>
    </div>
  );
}
